import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Inventory2,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';

const mostUsedIngredients = [
  { name: 'Water', count: 44, percent: 100 },
  { name: 'Sugar', count: 33, percent: 75 },
  { name: 'Salt', count: 28, percent: 64 },
  { name: 'Sunflower oil', count: 21, percent: 48 },
  { name: 'Pectin', count: 16, percent: 36 },
];

const leastUsedIngredients = [
  { name: 'Malt extract', count: 1 },
  { name: 'Pea protein', count: 2 },
  { name: 'Citrus fibre', count: 2 },
  { name: 'Rice starch', count: 3 },
];

function UsageBar({ ingredient }) {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.45 }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            fontSize: 11.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pr: 1,
          }}
        >
          {ingredient.name}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 800, fontSize: 10.8 }}
        >
          {ingredient.count}
        </Typography>
      </Stack>

      <Box
        sx={{
          height: 7,
          borderRadius: 999,
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${ingredient.percent}%`,
            height: '100%',
            borderRadius: 999,
            bgcolor: 'primary.main',
          }}
        />
      </Box>
    </Box>
  );
}

export default function ManagementIngredientAnalyticsVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 285,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inventory2 sx={{ fontSize: 16 }} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Ingredient usage analytics
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.2 }}
              >
                Usage frequency across formulations
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Usage analysis"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.45fr 1fr' },
            minHeight: 220,
          }}
        >
          {/* Most used */}
          <Box sx={{ p: 1.7 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.15 }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <TrendingUp sx={{ fontSize: 16, color: 'primary.main' }} />

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Most used ingredients
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: 'primary.main', fontSize: 10.5 }}
              >
                Top 5
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 1.35,
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Stack spacing={1}>
                {mostUsedIngredients.map((ingredient) => (
                  <UsageBar key={ingredient.name} ingredient={ingredient} />
                ))}
              </Stack>
            </Paper>


          </Box>

          {/* Least used */}
          <Box
            sx={{
              p: 1.7,
              borderLeft: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.016),
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.15 }}>
              <TrendingDown sx={{ fontSize: 16, color: 'primary.main' }} />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Top 5 least used ingredients
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                mb: 1.2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  columnGap: 1,
                  px: 1.25,
                  py: 0.85,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 800, fontSize: 10.3 }}
                >
                  Ingredient
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 800, fontSize: 10.3 }}
                >
                  Uses
                </Typography>
              </Box>

              {leastUsedIngredients.map((ingredient, index) => (
                <Box
                  key={ingredient.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.9,
                    borderBottom:
                      index !== leastUsedIngredients.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      fontSize: 11,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ingredient.name}
                  </Typography>

                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.28,
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 800,
                      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                      color: 'warning.dark',
                    }}
                  >
                    {ingredient.count}
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}